// utils.js
import axios from "axios";
import axiosRetry from 'axios-retry';
import {algo, enc, lib} from "crypto-js";
import {ref} from "vue";
import {isSessionExpired, markSessionExpired} from '@/lib/auth/sessionGuard.js';


// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = false; // Not recommended for production

axios.interceptors.request.use(config => {
    // Only add CSRF token to non-GET requests
    if (config.method !== 'get') {
        const csrfToken = getCsrfToken();
        if (csrfToken) {
            config.headers[getCsrfTokenHeaderName()] = csrfToken;
            config.headers['X-FADER'] = 'true'; // Custom header to indicate FADER request
        }
    }
    return config;
}, error => {
    console.error('Request error:', error);
    return Promise.reject(error);
});

export const formatFileSize = (bytes) => {
    if (isNaN(bytes)) return '-';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export const formatCurrency = (amount) => {
    if (isNaN(amount)) return '-';
    return new Intl.NumberFormat('no-NO', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
}

// Shared date parsing logic
const parseDate = (timestamp) => {
    if (!timestamp) return null;

    let date;

    // Handle different input formats
    if (typeof timestamp === 'string') {
        // Check if it's a numeric string (milliseconds)
        if (/^\d+$/.test(timestamp)) {
            date = new Date(parseInt(timestamp));
        } else {
            // Handle ISO 8601 format or other date strings
            date = new Date(timestamp);
        }
    } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
    } else {
        return null;
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
        return null;
    }

    return date;
}

export const formatDateTime = (timestamp) => {
    const date = parseDate(timestamp);
    if (!date) return '-';

    return date.toLocaleDateString('no-NO') + ' ' + date.toLocaleTimeString('no-NO', {timeStyle: "short"});
}

export const formatDate = (timestamp) => {
    const date = parseDate(timestamp);
    if (!date) return '-';

    return date.toLocaleDateString('no-NO', {dateStyle: "medium"});
}

export const formatShortTime = (timestamp) => {
    const date = parseDate(timestamp);
    if (!date) return '-';

    return date.toLocaleTimeString('no-NO', {timeStyle: "short"});
}

// Formatting of Zip Code (Postnummer).
// This function pads the zip code with leading zeros to ensure it is always 4 digits long.
export const formatZipCode = (postnummer) => {
    return String(postnummer).padStart(4, '0');
}

export const getHeadMetaContent = (name) => {
    const meta = document.querySelector(`meta[name="${name}"]`)
    if (!meta) {
        return '';
    } else {
        return meta.getAttribute('content') || '';
    }
}

export const getCsrfToken = () => {
    return getHeadMetaContent("_csrf");
}

export const getCsrfTokenHeaderName = () => {
    return getHeadMetaContent("_csrf_header") || "X-XSRF-TOKEN";
}

// Validations

/**
 * Builds the full API URL based on environment configuration.
 * In local/Storybook (default): Uses relative URLs (e.g., /api/billing/...)
 * In Storybook dev mode: Uses https://dev.api.samform.no/billing/v1/...
 * In Storybook prod mode: Uses https://api.samform.no/billing/v1/...
 * In dev/prod (Azure): Uses absolute URL based on VITE_API_BASE_URL
 *
 * @param {string} path - The API endpoint path (e.g., '/api/billing/validations/email')
 * @returns {string} - The full API URL
 */
export const buildApiUrl = (path) => {
    // Check if we're in Storybook mode
    const storybookMode = import.meta.env.VITE_STORYBOOK_MODE;

    if (storybookMode === 'development') {
        // Storybook development mode - use dev.api.samform.no
        const azurePath = path.replace(/^\/api\/([^/]+)\//, '/$1/v1/');
        return `https://dev.api.samform.no${azurePath}`;
    } else if (storybookMode === 'production') {
        // Storybook production mode - use api.samform.no
        const azurePath = path.replace(/^\/api\/([^/]+)\//, '/$1/v1/');
        return `https://api.samform.no${azurePath}`;
    }

    // Normal application mode (not Storybook with explicit mode)
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';

    // If no base URL is set (local/Storybook default), use relative path
    if (!apiBaseUrl) {
        return path;
    }

    // For Azure environments, convert /api/billing/... to /billing/v1/...
    // This assumes the path follows the pattern /api/{service}/...
    const azurePath = path.replace(/^\/api\/([^/]+)\//, '/$1/v1/');

    return `${apiBaseUrl}${azurePath}`;
};

// Validate email address (client-side regex)
export const validateEmail = (email) => {
    const regex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
    return regex.test(email);
};

// Validate email address with API (async)
export const validateEmailWithAPI = async (email) => {
    try {
        const apiUrl = buildApiUrl('/api/billing/validations/email');
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({email})
        });

        if (!response.ok) {
            console.warn('Email validation API failed with status:', response.status);
            const isValid = validateEmail(email);
            return {
                valid: isValid,
                suggestion: null,
                error: isValid ? null : 'Ugyldig e-postadresse',
                isFallback: true
            };
        }

        const data = await response.json();
        return {
            valid: data.valid || false,
            suggestion: data.suggestion || null,
            error: data.valid === false ? 'Ugyldig e-postadresse' : null
        };
    } catch (error) {
        console.warn('Email validation API failed, falling back to regex:', error);
        // Fallback to regex validation
        const isValid = validateEmail(email);
        return {
            valid: isValid,
            suggestion: null,
            error: isValid ? null : 'Ugyldig e-postadresse',
            isFallback: true
        };
    }
};

// Create debounced email validator composable
export const createDebouncedEmailValidator = (initialDelay = 5000, subsequentDelay = 2000) => {
    const emailValidationInProgress = ref(false);
    const emailValidationResult = ref(null);
    const hasValidatedBefore = ref(false);
    let debounceTimerId = null;

    const clearDebounce = () => {
        if (debounceTimerId) {
            clearTimeout(debounceTimerId);
            debounceTimerId = null;
        }
    };

    const validateEmail = (email) => {
        clearDebounce();

        if (!email || email.trim() === '') {
            emailValidationResult.value = null;
            return;
        }

        const delay = hasValidatedBefore.value ? subsequentDelay : initialDelay;

        debounceTimerId = setTimeout(async () => {
            emailValidationInProgress.value = true;
            try {
                emailValidationResult.value = await validateEmailWithAPI(email);
                hasValidatedBefore.value = true;
            } catch (error) {
                console.error('Email validation error:', error);
                emailValidationResult.value = {
                    valid: false,
                    error: 'Kunne ikke validere e-post'
                };
            } finally {
                emailValidationInProgress.value = false;
            }
        }, delay);
    };

    return {
        emailValidationInProgress,
        emailValidationResult,
        validateEmail,
        hasValidatedBefore,
        clearDebounce
    };
};

export const getToken = async () => {
    if (isSessionExpired()) return false
    try {
        const tokenResponse = await axios.get('/api/auth/token', {
            'axios-retry': { retries: 0 }
        })
        const tokenData = tokenResponse.data

        if (!tokenData) {
            console.warn('No token received from backend')
            return false
        }

        // If response is an object with a token property, extract it
        token = typeof tokenData === 'object' && tokenData.token ? tokenData.token : tokenData
    } catch (error) {
        if (error.response?.status === 401) {
            markSessionExpired()
            return false
        }
        window.Sentry?.addBreadcrumb({
            category: 'auth',
            message: 'getToken failed',
            level: 'warning',
            data: { status: error.response?.status, code: error.code },
        })
        console.error('Error:', error)
        return false
    }
    return token
}

let token;

axiosRetry(axios, {
    retries: 1,
    retryCondition: (error) => {
        return error.response?.status === 401 && !isSessionExpired()
    },
    onRetry: async () => {
        token = await getToken()
    }
})

export const getApi = async (url, {throwOnError = false} = {}) => {
    token = token ? token : await getToken();

    return await axios({
        url: url,
        method: 'get',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    })
        .catch((error) => {
            if (throwOnError) {
                throw error
            }
            console.error('Error:', error)
            return error
        });
}

export const postApi = async (url, data) => {
    token = token ? token : await getToken();

    return await axios({
        url: url,
        method: 'post',
        data: data,
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
        .then((response) => {
            return response
        })
        .catch((error) => {
            console.error('Error:', error)
            Promise.reject(error)
            return error
        });
}

export const putApi = async (url, data) => {
    token = token ? token : await getToken();

    return await axios({
        url: url,
        method: 'put',
        data: data,
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
        .then((response) => {
            return response
        })
        .catch((error) => {
            console.error('Error:', error)
            Promise.reject(error)
            return error
        });
}

export const patchApi = async (url, data) => {
    token = token ? token : await getToken();

    return await axios({
        url: url,
        method: 'patch',
        data: data,
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
        .then((response) => {
            return response
        })
        .catch((error) => {
            console.error('Error:', error)
            Promise.reject(error)
            return error
        });
}

export const postApiStream = async (url, data) => {
    token = token ? token : await getToken();
    return axios({
        url: url,
        method: 'post',
        data: data,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
        },
        responseType: 'stream',
        adapter: 'fetch'
    })
        .then((response) => {
            return response
        })
        .catch((error) => {
            console.error('Error:', error)
            return false
        });
}

/**
 * Calculates the MD5 hash of a large ArrayBuffer in chunks to handle large data efficiently.
 *
 * @param {ArrayBuffer} buffer - The input ArrayBuffer to hash.
 * @param {(progress: {processedBytes: number}) => void} [onProgress] - Optional callback function invoked after each chunk is processed. Receives a progress object with the number of bytes processed so far.
 * @param {number} [chunkSize=1048576] - The size of each chunk (in bytes) to process at a time. Defaults to 1MB.
 * @return {Promise<{base64Hash: string, uint8Hash: Uint8Array}>} A promise that resolves to an object containing the MD5 hash in two formats:
 * - `base64Hash`: The hash encoded as a base64 string.
 * - `uint8Hash`: The hash represented as a Uint8Array.
 */
export async function calculateMD5ForLargeArrayBuffer(buffer, onProgress, chunkSize = 1024 * 1024) {
    const md5Hasher = algo.MD5.create();
    const data = new Uint8Array(buffer);
    let processedChunks = 0;

    // Process in chunks
    for (let offset = 0; offset < data.length; offset += chunkSize) {
        const end = Math.min(offset + chunkSize, data.length);
        const chunk = data.slice(offset, end);
        const wordArray = lib.WordArray.create(chunk);
        md5Hasher.update(wordArray);

        processedChunks++;
        if (onProgress) {
            onProgress({
                processedBytes: end,
            });
        }

        await new Promise(resolve => setTimeout(resolve, 0));
    }

    const hash = md5Hasher.finalize()
    const base64Representation = hash.toString(enc.Base64)
    return {
        base64Hash: base64Representation,
        uint8Hash: Uint8Array.from(atob(base64Representation), c => c.charCodeAt(0)),
    };
}

export const isMobileScreen = () => window.innerWidth < 1024;
